import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from "maplibre-gl";
import Map, { Source, Layer } from 'react-map-gl';
import 'maplibre-gl-draw/dist/mapbox-gl-draw.css';
import './TrackMap.css';
import { bbox, circle } from '@turf/turf';
import DrawControl from "./DrawControl";
import { TrackMapDrawStyle } from "./TrackMapDrawStyle";

const key = 'L67Uis9Da5SZCWwoyVMF';
const mapStyleLight = `https://api.maptiler.com/maps/a413b951-43d9-4d7b-9f84-04f544f59f72/style.json?key=${key}`;
const INITIAL_VIEW_STATE = {
    longitude: 1.99243,
    latitude: 48.75927,
    zoom: 17,
};

const TrackMap = ({ mapRef, handleMapClick, geojson, parseCompleteGeojson, radius }) => {
    const [features, setFeatures] = useState({});
    const [searchRadiusGeojson, setSearchRadiusGeojson] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false); // New state to track if the map is fully loaded
    const drawRef = useRef(null);
    const mouseRightClickEnableRef = useRef(true);

    const onUpdate = useCallback((e) => {
        setFeatures((currFeatures) => {
            const newFeatures = { ...currFeatures };
            e.features.forEach(f => {
                newFeatures[f.id] = f; // Update or add the feature
            });
            return newFeatures;
        });
    }, []);

    const onDelete = useCallback((e) => {
        setFeatures((currFeatures) => {
            const newFeatures = { ...currFeatures };
            e.features.forEach(f => {
                delete newFeatures[f.id]; // Remove the feature
            });
            return newFeatures;
        });
    }, []);

    const onModeChange = useCallback((e) => {
        console.log('TrackMap / onModeChange', e);
        if (e.mode.includes('draw_')) {
            mouseRightClickEnableRef.current = false;
        }
        else {
            setTimeout(() => {
                mouseRightClickEnableRef.current = true;
            }, 500);
        }
    }, [mouseRightClickEnableRef]);

    useEffect(() => {
        if (Object.keys(features).length > 0) {
            parseCompleteGeojson(features);
        } else {
            parseCompleteGeojson(null);
        }
    }, [features, parseCompleteGeojson]);

    const loadGeoJson = useCallback((geojson) => {
        if (!mapLoaded || !drawRef.current) return; // Ensure map is loaded and refs are ready

        const map = mapRef.current.getMap();
        const draw = drawRef.current;

        draw.deleteAll();

        if (geojson) {
            draw.add(geojson);

            const allFeatures = draw.getAll().features;
            const initialFeatures = {};
            allFeatures.forEach(f => {
                initialFeatures[f.id] = f;
            });
            setFeatures(initialFeatures);

            const geojsonBoundsArray = bbox(geojson);
            const geojsonBounds = new maplibregl.LngLatBounds(
                [geojsonBoundsArray[0], geojsonBoundsArray[1]],
                [geojsonBoundsArray[2], geojsonBoundsArray[3]]
            );

            if (!geojsonBounds.isEmpty()) {
                map.fitBounds(geojsonBounds, { padding: 200 });
            }
        } else {
            setFeatures({});
            setSearchRadiusGeojson(null);
        }
    }, [mapLoaded, mapRef]);

    const handleRightClick = useCallback((e) => {
        if (!mouseRightClickEnableRef.current) return;

        e.preventDefault();
        const { lngLat } = e;
        handleMapClick(lngLat);

        const options = { steps: 64, units: 'kilometers' };
        const searchRadiusGeoJSON = circle([lngLat.lng, lngLat.lat], radius, options);

        setSearchRadiusGeojson(searchRadiusGeoJSON);
    }, [handleMapClick, radius, mouseRightClickEnableRef]);

    useEffect(() => {
        if (!mapLoaded || !drawRef.current) return;

        const map = mapRef.current.getMap();
        const draw = drawRef.current;

        // Initialize map controls and behavior only once
        draw.deleteAll();
        map.addControl(new maplibregl.NavigationControl());

        // Remove any existing listener to avoid duplicates
        map.off('contextmenu', handleRightClick);
        map.on('contextmenu', handleRightClick);

        // Cleanup function to remove the event listener when component unmounts or radius changes
        return () => {
            map.off('contextmenu', handleRightClick);
        };
    }, [mapLoaded, handleRightClick]);

    useEffect(() => {
        loadGeoJson(geojson);
    }, [geojson, loadGeoJson]);

    return (
        <div style={{ width: "100%", height: "calc(100vh - 170px)" }}>
            <Map
                ref={mapRef}
                mapLib={maplibregl}
                initialViewState={INITIAL_VIEW_STATE}
                style={{ width: '100%', height: '100%' }}
                mapStyle={mapStyleLight}
                onLoad={() => setMapLoaded(true)} // Set mapLoaded to true when the map is fully loaded
            >
                <DrawControl
                    ref={drawRef}
                    position="top-right"
                    displayControlsDefault={true}
                    controls={{
                        polygon: true,
                        trash: true
                    }}
                    onCreate={onUpdate}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onModeChange={onModeChange}
                    styles={TrackMapDrawStyle}
                />

                {searchRadiusGeojson && (
                    <Source id="search-radius" type="geojson" data={searchRadiusGeojson}>
                        <Layer
                            id="search-radius-fill"
                            type="fill"
                            paint={{
                                'fill-color': 'transparent',
                                'fill-opacity': 0
                            }}
                        />
                        <Layer
                            id="search-radius-outline"
                            type="line"
                            paint={{
                                'line-color': '#00ff00',
                                'line-width': 2
                            }}
                        />
                    </Source>
                )}
            </Map>
        </div>
    );
};

export default TrackMap;
